<template>
  <hr-form-dialog
    ref="dialog"
    size="sm"
    :title="title"
    @hide="onDialogHide"
    @submit="onSave"
  >
    <div class="row q-col-gutter-md">
      <p class="col-12 q-ma-none">
        {{ subTitle }}
      </p>
      <template
        v-for="[tabKey, tabLabel] in displayedTabs"
        :key="tabKey"
      >
        <hr-form-section-header
          :label="$t(tabLabel)"
          class="col-12"
        />
        <div class="col-12">
          <hr-checkbox
            v-for="(field, idx) in fieldsByTab[tabKey]"
            :key="field.id"
            v-model="fieldsByTab[tabKey][idx].isSelected"
            :label="field.label"
            class="full-width"
          />
        </div>
      </template>
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="onCancelClick"
      />
      <hr-confirm-btn
        type="submit"
        :label="submitButtonLabel"
      />
    </template>
  </hr-form-dialog>
</template>

<script setup>
import { groupBy } from 'lodash';
import { DATA_FIELD_TYPES } from 'src/constants/dynamicFields';
import {
  HrCheckbox,
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrFormSectionHeader,
} from 'src/design-system';
import { dataFields } from 'src/store/dataFields';
import { computed, ref, watch } from 'vue';

const { visibleProgramDataFields } = dataFields();

const emit = defineEmits(['hide', 'ok']);

const dialog = ref(null);
const fieldsByTab = ref({});

const tabs = {
  treatmentTab: 'settings.clientData.editDataField.optionFieldLocation.treatmentTab',
  instructionsTab: 'settings.clientData.editDataField.optionFieldLocation.instructionsTab',
};

defineOptions({ name: 'BulkManageDataFieldsDialog' });

const displayedTabs = computed(() => {
  return Object.entries(tabs)
    .filter(([tabKey]) => fieldsByTab.value[tabKey] && fieldsByTab.value[tabKey].length);
}); // displayedTabs()

defineProps({
  title: {
    type: String,
    required: true,
  },
  subTitle: {
    type: String,
    required: true,
  },
  submitButtonLabel: {
    type: String,
    required: true,
  },
  isRemoveMode: {
    type: Boolean,
    default: false,
  },
});

const show = () => {
  dialog.value.show();
};

const hide = () => {
  if (dialog.value) {
    dialog.value.hide();
  }
};

const onDialogHide = () => {
  emit('hide');
};

const onCancelClick = () => {
  hide();
};

const onSave = () => {
  const selectedFields = Object.values(fieldsByTab.value)
    .flat()
    .filter((field) => field.isSelected)
    .map(({ id, hideByDefault }) => ({ id, hideByDefault }));

  emit('ok', selectedFields);
  hide();
}; // onSave()

const isValidDataFieldType = (dataFieldType) => {
  return DATA_FIELD_TYPES.includes(dataFieldType);
};

const shouldShowDataField = (dataField) => {
  return isValidDataFieldType(dataField.type);
};

const filteredDataFields = computed(() => {
  return visibleProgramDataFields.filter((field) => shouldShowDataField(field));
});

const initLocalFields = () => {
  fieldsByTab.value = groupBy(
    filteredDataFields.value.map((field) => ({
      id: field.id,
      label: field.label,
      isSelected: false, // they should be all unselected initailly
      hideByDefault: field.hideByDefault || false,
      fieldLocation: field.fieldLocation || 'treatmentTab',
    })),
    'fieldLocation',
  );
};

watch(
  visibleProgramDataFields,
  async () => {
    initLocalFields();
  },
  { immediate: true },
);

defineExpose({ show, hide });
</script>
